import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ kicker, lines, intro, primaryCta, secondaryCta, image, imageAlt, testidPrefix = "hero" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative flex min-h-[92vh] items-end overflow-hidden" data-testid={`${testidPrefix}-section`}>
      <motion.div style={{ y }} className="absolute inset-0 scale-[1.15]">
        <img src={image} alt={imageAlt} className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/65" />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-20 pt-40 sm:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.35em] text-accent"
          data-testid={`${testidPrefix}-kicker`}
        >
          {kicker}
        </motion.p>

        <h1
          className="font-display text-5xl uppercase leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-[5.5rem]"
          data-testid={`${testidPrefix}-headline`}
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
              >
                {line.map((seg, j) => (
                  <span key={j} className={seg.accent ? "text-accent" : undefined}>
                    {seg.t}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-xl text-lg leading-relaxed text-white/80"
          data-testid={`${testidPrefix}-intro`}
        >
          {intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <a
            href={primaryCta.href}
            target={primaryCta.href.startsWith("http") ? "_blank" : undefined}
            rel={primaryCta.href.startsWith("http") ? "noopener noreferrer" : undefined}
            data-testid={`${testidPrefix}-primary-cta`}
            className="inline-flex items-center gap-2 bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-accent-dark hover:shadow-[0_0_32px_rgba(244,123,32,0.45)]"
          >
            {primaryCta.label}
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href={secondaryCta.href}
            target={secondaryCta.href.startsWith("http") ? "_blank" : undefined}
            rel={secondaryCta.href.startsWith("http") ? "noopener noreferrer" : undefined}
            data-testid={`${testidPrefix}-secondary-cta`}
            className="group inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.15em] text-white/80 underline decoration-white/30 underline-offset-8 transition-colors duration-200 hover:text-accent hover:decoration-accent"
          >
            {secondaryCta.label}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
